/**
 * 商户号解析纯函数（AC-3 / SPRINT-WEB-CRITICAL + Web Precision Followup）。
 *
 * 背景：`/auth/me` 失败时旧逻辑回落 `DEFAULT_MERCHANT_ID = 20`，会把写请求打到
 * 不存在的默认商户；历史会话也可能残留 `merchantId: 20`。本模块统一 fail-close：
 *
 * - 身份只从 `me.merchantId` 解析（`session.merchantId` 仅作缓存，不可信）；
 * - 无 me / me 缺 merchantId / 非正整数 / 超 Long 上限 → null（调用方必须拦截写请求）；
 * - 后端门户 Long 出站为 string（`PortalJacksonModules`），解析结果**恒为十进制字符串**，
 *   BigInt 只做瞬时越界比较，绝不写入 session（JSON 不能序列化 BigInt）；
 * - 19 位雪花 ID 超过 `Number.MAX_SAFE_INTEGER`，禁止 `Number()` 往返（会丢尾数）。
 *
 * 零 React / 零 client 依赖（node --experimental-strip-types 可直测）。
 */

/** 商户身份未加载时的统一错误文案（positions / feeItems 写路径共用） */
export const MERCHANT_IDENTITY_MISSING_ERROR = '商户身份未加载，请重新登录后重试';

/** 后端 Long 上限（MySQL BIGINT 有符号最大值）；BigInt 仅用于瞬时比较，不落 session */
const MAX_MERCHANT_ID = 9223372036854775807n;

/** `/auth/me` 视图最小面 */
export type MerchantMeLike = { merchantId?: unknown } | null | undefined;

/** 商户会话最小面（`merchantId` 为历史缓存，不参与身份解析） */
export type MerchantSessionLike = {
  merchantId?: unknown;
  me?: MerchantMeLike;
} | null | undefined;

/**
 * 将后端返回的商户号归一为精确十进制字符串。
 * - string：trim 后纯数字，且 ≤ `9223372036854775807`（BigInt 精确比较，不截断）
 * - number：必须 `Number.isSafeInteger` 且 > 0（已丢精度的 number 不可恢复 → null）
 * - 其余（null/undefined/小数/负数/空串/科学计数法/超 Long）→ null
 */
export function toMerchantIdString(raw: unknown): string | null {
  if (typeof raw === 'number') {
    return Number.isSafeInteger(raw) && raw > 0 ? String(raw) : null;
  }
  if (typeof raw === 'string') {
    const text = raw.trim();
    if (!/^\d+$/.test(text)) {
      return null;
    }
    try {
      const value = BigInt(text);
      if (value <= 0n || value > MAX_MERCHANT_ID) {
        return null;
      }
      return text;
    } catch {
      return null;
    }
  }
  return null;
}

/** 由 `/auth/me` 视图解析商户号；无 me / 缺字段 / 非法 → null */
export function sessionMerchantIdFromMe(me: MerchantMeLike): string | null {
  if (!me) return null;
  return toMerchantIdString(me.merchantId);
}

/**
 * 由会话解析可信商户号：只认 `me.merchantId`。
 * 历史残留的 `session.merchantId = 20`（me 为 null）必须返回 null。
 */
export function resolveMerchantIdFromSession(session: MerchantSessionLike): string | null {
  return sessionMerchantIdFromMe(session?.me);
}
